import { useState } from "react";
import { ChevronDown, ChevronRight, Activity, Copy, Check } from "lucide-react";
import type { Stats, Versions } from "@/lib/api/assistantV2";

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-slate-400">{label}:</span>
      <span className="text-slate-700 tabular-nums">{value}</span>
    </div>
  );
}

/** Run id that copies to the clipboard on click, with a subtle affordance. */
export function CopyableRunId({ runId }: { runId: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(runId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — fail silently.
    }
  };

  return (
    <div className="flex items-baseline gap-1">
      <span className="text-slate-400">run:</span>
      <button
        type="button"
        onClick={copy}
        title="Click to copy run id"
        className="group inline-flex items-baseline gap-1 text-slate-700 tabular-nums underline decoration-dotted decoration-slate-300 underline-offset-2 hover:text-violet-600 hover:decoration-violet-400 cursor-pointer"
      >
        {runId}
        {copied ? (
          <Check className="h-3 w-3 self-center text-emerald-600" />
        ) : (
          <Copy className="h-3 w-3 self-center text-slate-400 opacity-60 group-hover:opacity-100 group-hover:text-violet-500" />
        )}
      </button>
    </div>
  );
}

/** Compact run footer: model, latency, retries, finish reason, token usage. */
export function StatsPanel({
  stats,
  versions,
}: {
  stats: Stats;
  versions: Versions | null;
}) {
  const [open, setOpen] = useState(false);
  const usage = stats.usage;
  const truncated = stats.finish_reason === "length";

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-slate-50 text-xs"
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-600">
          <span className="flex items-center gap-1.5 font-medium text-slate-700">
            <Activity className="h-3.5 w-3.5 text-slate-500" />
            Run
          </span>
          <Stat label="model" value={stats.model || "—"} />
          <Stat label="latency" value={`${stats.latency_ms} ms`} />
          <Stat
            label="retries"
            value={`${stats.retry_count}/${stats.max_retries}`}
          />
          {stats.finish_reason && (
            <span
              className={`flex items-baseline gap-1 ${
                truncated ? "text-amber-700" : ""
              }`}
            >
              <span className="text-slate-400">finish:</span>
              {stats.finish_reason}
              {truncated && " (truncated)"}
            </span>
          )}
          {usage?.total_tokens != null && (
            <Stat label="tokens" value={String(usage.total_tokens)} />
          )}
          {usage?.cost != null && (
            <Stat label="cost" value={`$${usage.cost.toFixed(4)}`} />
          )}
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />
        )}
      </button>
      {open && (
        <div className="border-t border-slate-200 px-4 py-2.5 text-xs space-y-2">
          {usage && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-600">
              <Stat label="input tokens" value={String(usage.input_tokens ?? "—")} />
              <Stat
                label="output tokens"
                value={String(usage.output_tokens ?? "—")}
              />
              <Stat label="total tokens" value={String(usage.total_tokens ?? "—")} />
            </div>
          )}
          {versions && (
            <div className="space-y-1 text-slate-600">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <CopyableRunId runId={versions.run_id} />
                <Stat label="created" value={versions.created_at} />
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <Stat label="sdk" value={versions.sdk_version} />
                <Stat label="api" value={versions.api_version} />
                <Stat label="capability" value={versions.capability_version} />
              </div>
              {versions.studies_used.length > 0 && (
                <div className="flex items-baseline gap-1 flex-wrap">
                  <span className="text-slate-400">studies:</span>
                  {versions.studies_used.map((s, i) => (
                    <span
                      key={i}
                      className="font-mono text-slate-700 bg-slate-100 rounded px-1.5 py-0.5"
                    >
                      {s.study_id}@{s.version}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
